<template>
  <AreYouStillThereModal
    :visible="showModal"
    :duration="modalDuration"
    @stay="handleStay"
    @logout="handleLogout"
  />
</template>

<script>
import InactivityManager from '@/services/InactivityManager'
import AreYouStillThereModal from './AreYouStillThereModal.vue'

export default {
  name: 'InactivityWatcher',
  components: {
    AreYouStillThereModal,
  },
  props: {
    autoStart: {
      type: Boolean,
      default: true,
    },
    enabled: {
      type: Boolean,
      default: true,
    },
    inactivityTimeout: {
      type: Number,
      default: 5 * 60 * 1000,
    },
    modalDuration: {
      type: Number,
      default: 30,
    },
  },
  data() {
    return {
      inactivityManager: null,
      showModal: false,
      tabInactiveTime: null,
      visibilityListenerAttached: false,
      activityInterval: null,
    }
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.common.isSignedIn
    },
    shouldMonitorSession() {
      return this.enabled
    },
  },
  watch: {
    enabled(newVal) {
      if (newVal && this.isUserLoggedIn) {
        this.startWatching()
      } else {
        this.stopWatching()
      }
    },
    inactivityTimeout(newVal) {
      if (this.inactivityManager) {
        this.inactivityManager.setTimeout(newVal)
      }
    },
    isUserLoggedIn(newVal) {
      if (newVal && this.enabled) {
        this.startWatching()
      } else {
        this.stopWatching()
        this.hideModal()
      }
    },
    shouldMonitorSession(newVal) {
      if (newVal) {
        this.attachVisibilityListener()
      } else {
        this.detachVisibilityListener()
      }
    },
  },
  mounted() {
    this.initializeManager()

    if (this.shouldMonitorSession) {
      this.attachVisibilityListener()
    }

    if (this.autoStart && this.enabled && this.isUserLoggedIn) {
      this.startWatching()
    }
  },
  beforeDestroy() {
    this.cleanup()
    this.detachVisibilityListener()
  },
  methods: {
    attachVisibilityListener() {
      if (!this.visibilityListenerAttached) {
        document.addEventListener('visibilitychange', this.handleVisibilityChange)
        this.visibilityListenerAttached = true
      }
    },
    cleanup() {
      this.stopWatching()
      this.stopActivityTracking()
      if (this.inactivityManager) {
        this.inactivityManager = null
      }
    },
    async handleInactivity() {
      if (this.tabInactiveTime) return

      await this.$store.dispatch('common/checkIsAuthenticated')
      if (!this.isUserLoggedIn) {
        this.handleLogout()
        return
      }

      this.showModal = true

      this.$emit('inactivity-detected')
    },
    detachVisibilityListener() {
      if (this.visibilityListenerAttached) {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange)
        this.visibilityListenerAttached = false
      }
    },
    async handleLogout() {
      this.hideModal()
      this.stopWatching()

      try {
        // Temporarily disable route guards to allow forced navigation
        this.$store.commit('common/setBypassNavigationGuards', true)

        this.$emit('user-logout')

        await this.$store.dispatch('common/signOut', false)

        if (this.$route.name !== 'TheLogin') {
          this.$router.push({ name: 'TheLogin' })
        }
      } catch (error) {
        if (this.$route.name !== 'TheLogin') {
          this.$router.push({ name: 'TheLogin' })
        }
      } finally {
        setTimeout(() => {
          this.$store.commit('common/setBypassNavigationGuards', false)
        }, 500)
      }
    },
    async handleStay() {
      this.hideModal()
      this.resetInactivityTimer()

      await this.$store.dispatch('common/checkIsAuthenticated')

      this.$emit('user-stayed')
    },
    hideModal() {
      this.showModal = false
    },
    initializeManager() {
      this.inactivityManager = new InactivityManager({
        timeout: this.inactivityTimeout,
        callback: this.handleInactivity,
      })
    },
    reset() {
      this.resetInactivityTimer()
    },
    resetInactivityTimer() {
      if (this.inactivityManager && this.inactivityManager.getIsActive()) {
        this.inactivityManager.resetTimer()
      }
    },
    startActivityTracking() {
      this.stopActivityTracking()

      this.activityInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          localStorage.setItem('lastGlobalActivity', new Date().getTime().toString())
        }
      }, 60000)
    },
    startWatching() {
      if (!this.inactivityManager || !this.enabled || !this.isUserLoggedIn) {
        return
      }

      this.inactivityManager.start()
      this.startActivityTracking()
    },
    stop() {
      this.stopWatching()
    },
    stopActivityTracking() {
      if (this.activityInterval) {
        clearInterval(this.activityInterval)
        this.activityInterval = null
      }
    },
    stopWatching() {
      if (this.inactivityManager) {
        this.inactivityManager.stop()
      }
      this.stopActivityTracking()
    },
    handleVisibilityChange() {
      if (!this.shouldMonitorSession) return

      if (document.visibilityState === 'hidden') {
        this.tabInactiveTime = new Date().getTime()
        // Store last time this tab was active
        localStorage.setItem('lastTabInactiveTime', this.tabInactiveTime.toString())
      } else if (document.visibilityState === 'visible' && this.tabInactiveTime) {
        const currentTime = new Date().getTime()

        // Check if another tab has been active while this one was inactive
        const lastGlobalActivity = parseInt(localStorage.getItem('lastGlobalActivity') || '0', 10)

        // If another tab has been active after this tab went inactive, don't show modal
        if (lastGlobalActivity > this.tabInactiveTime) {
          // Another tab was active, just reset timer
          this.resetInactivityTimer()
        } else {
          // No other tabs were active, check inactivity as usual
          const timeAwayMs = currentTime - this.tabInactiveTime
          const sessionTimeoutMs = this.$store.state.common.sessionDurationInMinutes * 60 * 1000

          if (timeAwayMs >= sessionTimeoutMs) {
            this.handleLogout()
          } else {
            this.resetInactivityTimer()
          }
        }

        this.tabInactiveTime = null
      }

      // Update global activity timestamp when tab is visible
      if (document.visibilityState === 'visible') {
        localStorage.setItem('lastGlobalActivity', new Date().getTime().toString())
      }
    },
  },
}
</script>
